import { InvitationStageFilter, InvitationStageBadge, type InvitationStage } from "../invitation-stage-filter.tsx";
import { displayFormatDate } from "../../../../../utils/helpers.js";
import styles from "./consultant.module.css";
import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { Spinner, Button, ResponsiveDataTable, type ResponsiveColumn, type SelectOption } from "../../../ui/index.ts";
import { EmptyStateMessage } from "../../../shared/empty-state-message/empty-state-message.tsx";
import { InviteConsultantModal } from "./invite-consultant-modal.tsx";
import { usePreJoinInvitations } from "../hooks/use-pre-join-invitations.ts";
import { showError } from "../../../../utils/toast.ts";
import type { OnboardInvitationRow } from "../schemas/onboard-invitation-row.schema.ts";
import type { ConsultantType } from "./types.ts";

interface Props {
  label: string;
  region: "INDIA" | "US";
  consultantType: ConsultantType;
  requestorOptions: SelectOption[];
  active: boolean;
}

const COLUMNS: ResponsiveColumn<OnboardInvitationRow>[] = [
  { id: "name", header: "Consultant Name", render: (row) => String(row.name ?? row.employeeName ?? row.consultantName ?? "") },
  { id: "email", header: "Email", render: (row) => String(row.email ?? row.emailId ?? row.personalEmail ?? "") },
  {
    id: "createdOn",
    header: "Invitation Date",
    sortable: true,
    render: (row) => row.createdOn ? displayFormatDate(row.createdOn) : "?",
    getSortValue: (row) => row.createdOn ? new Date(row.createdOn).getTime() : 0,
    getTitleText: (row) => row.createdOn ? displayFormatDate(row.createdOn) : "?",
  },
  {
    id: "status",
    header: "Status",
    render: (row) => <InvitationStageBadge stage={row.onboardingStage} />,
  },];

export function ConsultantCategoryPanel({ label, region, consultantType, requestorOptions, active }: Props) {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<InvitationStage>("ALL");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const apiType = consultantType === "vendor" ? "VENDOR_BASED" : region === "INDIA" ? "INDIAN_CONSULTANT" : "DIRECT";
  const { rows, totalElements, loading, error, refetch } = usePreJoinInvitations("", page, pageSize, apiType, active, stage);
  useEffect(() => { if (error) showError(error); }, [error]);
  return (
    <>
      <div className={styles.stageFilter}><InvitationStageFilter id={`consultant-${apiType}-stage`} value={stage} includeAgencyPending={region === "US" && consultantType === "vendor"} onChange={(value) => { setStage(value); setPage(1); }} />{active && <Button startIcon={<Plus size={16} />} onClick={() => setOpen(true)}>Invite Consultant</Button>}</div>
  
      {loading ? <div className={styles.invitationLoader} role="status" aria-label="Loading invitations"><Spinner /></div> : <ResponsiveDataTable<OnboardInvitationRow>
        columns={COLUMNS}
        data={rows}
        serialNumber
        page={page}
        pageSize={pageSize}
        totalItems={totalElements}
        onPageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
        totalLabel={`Total Invites: ${totalElements}`}
        aria-label={label}
        emptyState={<EmptyStateMessage message={error ? "Unable to load invitations." : "No consultant invitations yet."} />}
      />}
      {open && <InviteConsultantModal region={region} consultantType={consultantType} requestorOptions={requestorOptions} onClose={() => setOpen(false)} onSave={() => { setOpen(false); if (page === 1) void refetch(); else setPage(1); }} />}
    </>
  );
}
